import { useEffect, useState } from 'react';

import { RestService } from '../../../lib/utils/api.service';
import { STARTERS_PATH } from './chatbot.constants';

interface IUseChatStartersResult {
  starters: readonly string[];
  loading: boolean;
}

export interface IUseChatStartersOptions {
  /** Skip the fetch until the panel is opened (avoids work on every page mount). */
  enabled: boolean;
  /** Bearer token for the proxy when the visitor is signed in. */
  authToken?: string;
  /** Lets the server check the report card's association allowlist. */
  playerAssessmentId: string;
  /** ?token= from the URL when the visitor is unauthenticated. */
  token?: string;
}

export const useChatStarters = (
  { enabled, authToken, playerAssessmentId, token }: IUseChatStartersOptions
): IUseChatStartersResult => {
  const [starters, setStarters] = useState<readonly string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || starters.length > 0) return;
    const controller = new AbortController();
    setLoading(true);

    const params = new URLSearchParams({ playerAssessmentId });
    if (token) params.set('token', token);
    RestService.get(`${STARTERS_PATH}?${params}`, authToken, { signal: controller.signal })
      .then((data: { starters?: unknown }) => {
        const list = data && Array.isArray(data.starters) ? data.starters.filter((v) => typeof v === 'string') : [];
        setStarters(list as readonly string[]);
      })
      .catch((err) => {
        if ((err as Error)?.name === 'AbortError') return;
        // Starter chips are non-critical — a fetch failure stays console-only and
        // the input bar still works (see CLAUDE.md error rule).
        console.error('useChatStarters fetch failed:', err);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [enabled, authToken, playerAssessmentId, token, starters.length]);

  return { starters, loading };
};
